/**
 * Minimal PCD reader for the slot point-cloud viewer (ADR 284): binary,
 * FIELDS x y z, float32 — the exact shape the iC3 side cameras write. Pure
 * (no three.js/React) so the byte handling is unit-tested; anything else
 * (ascii PCDs, extra fields) is a hard null, never a garbled cloud.
 */
export interface Pcd {
  /** Flat xyz triplets, METERS (the wire is mm), NaN/zero returns dropped. */
  positions: Float32Array;
  count: number;
}

export function parsePcd(buf: ArrayBuffer): Pcd | null {
  const bytes = new Uint8Array(buf);
  // Header is ASCII lines up to and including "DATA ...\n".
  let end = -1;
  for (let i = 0; i < Math.min(bytes.length, 2048); i++) {
    if (bytes[i] === 0x0a) {
      const line = new TextDecoder().decode(bytes.subarray(0, i));
      const m = /(^|\n)DATA (\w+)\s*$/.exec(line);
      if (m) { end = i + 1; if (m[2] !== "binary") return null; break; }
    }
  }
  if (end < 0) return null;
  const header = new TextDecoder().decode(bytes.subarray(0, end));
  const fields = /FIELDS (.+)/.exec(header)?.[1].trim();
  const points = Number(/POINTS (\d+)/.exec(header)?.[1]);
  if (fields !== "x y z" || !Number.isFinite(points)) return null;
  const need = points * 12;
  if (buf.byteLength - end < need) return null;
  const raw = new Float32Array(buf.slice(end, end + need));
  // Drop NaN and zero returns; convert mm → m.
  const out = new Float32Array(raw.length);
  let n = 0;
  for (let i = 0; i < points; i++) {
    const x = raw[i * 3], y = raw[i * 3 + 1], z = raw[i * 3 + 2];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    if (Math.abs(x) + Math.abs(y) + Math.abs(z) < 1e-6) continue;
    out[n * 3] = x / 1000; out[n * 3 + 1] = y / 1000; out[n * 3 + 2] = z / 1000;
    n++;
  }
  return { positions: out.subarray(0, n * 3), count: n };
}

/** Per-point colors on the viridis heat ramp by height (z), flat rgb triplets.
 *  Same ramp as the occupancy surfaces — one color language for density. */
export function heightColors(positions: Float32Array, ramp: readonly string[]): Float32Array {
  const n = positions.length / 3;
  const colors = new Float32Array(n * 3);
  let zMin = Infinity, zMax = -Infinity;
  for (let i = 0; i < n; i++) {
    const z = positions[i * 3 + 2];
    if (z < zMin) zMin = z;
    if (z > zMax) zMax = z;
  }
  const span = Math.max(1e-6, zMax - zMin);
  const rgb = ramp.map((hex) => [1, 3, 5].map((o) => parseInt(hex.slice(o, o + 2), 16) / 255));
  for (let i = 0; i < n; i++) {
    const t = (positions[i * 3 + 2] - zMin) / span;
    const s = Math.min(rgp1(ramp.length) - 1e-6, t * rgp1(ramp.length));
    const j = Math.floor(s), f = s - j;
    const lo = rgb[j], hi = rgb[Math.min(j + 1, ramp.length - 1)];
    colors[i * 3] = lo[0] + (hi[0] - lo[0]) * f;
    colors[i * 3 + 1] = lo[1] + (hi[1] - lo[1]) * f;
    colors[i * 3 + 2] = lo[2] + (hi[2] - lo[2]) * f;
  }
  return colors;
}

/** Segments of the ramp (stops − 1), guarded for a single-stop ramp. */
function rgp1(stops: number): number {
  return Math.max(1, stops - 1);
}
